import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';

import {ErrorBoundary} from '@sentry/react';
import debounce from 'lodash.debounce';
import {PB_ID_SUGGEST_ERROR, VALIDATION_ERRORS} from 'src/components/account-information/constants';
import {ErrorFlatButton} from 'src/components/error-flat-button';
import {WidthItem} from 'src/components/width-item';
import {ValidatedControl, ValidationContext} from 'src/contexts/validation-context';
import {runCrossReferenceSearch} from 'src/openapi/admin/apis/RunCrossReferenceSearchApi';
import {useAppDispatch} from 'src/store/hooks';

import {Autosuggest, AutosuggestItem, AutosuggestNoResults} from '@pb/kit-component-autosuggest-v2';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import Loading, {Types as LoadingTypes} from '@pb/kit-component-loading';
import {makeCancelable} from '@pb/kit-util-make-cancelable';


import type {Cancelable} from '@pb/kit-util-make-cancelable';
import type {ValidationContextType} from 'src/contexts/validation-context';
import type {ReferenceEntity} from 'src/openapi/admin/models/ReferenceEntity';
import type {ReferenceEntityType} from 'src/openapi/admin/models/ReferenceEntityType';


type Props = {
    id: number,
    value: string,
    onSelect: (pbId: string, id: number) => void,
    onRemove: (id: number) => void,
    pbIds: Array<string>,
    disabled?: boolean,
    width?: number,
    entityType: ReferenceEntityType,
    placeholder?: string,
};

export const SearchPbId: React.FC<Props> = props => {
    const {id, value, onSelect, pbIds, onRemove, disabled, entityType, width = 300, placeholder = 'PB RTS ID'} = props;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedOnSelect = useCallback(debounce(onSelect, 100), []);
    const apiDispatch = useAppDispatch();
    const cancellableRef = useRef<Cancelable>({cancel: () => undefined});

    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [suggestedPBIds, setSuggestedPBIds] = useState<Array<ReferenceEntity> | null>([]);
    const [shown, setShown] = useState(false);

    const {addError, removeError} = useContext<ValidationContextType>(ValidationContext);

    useEffect(() => {
        setQuery(value || '');
    }, [value]);

    useEffect(() => {
        if (query) {
            setBusy(true);
            cancellableRef.current = makeCancelable(
                apiDispatch(runCrossReferenceSearch({
                    searchByName: query,
                    entityType,
                })),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (response: any) => {
                    if (response.payload.success) {
                        setSuggestedPBIds(response.payload.data
                            .filter(({pbId}: {pbId: string}) => !pbIds.includes(pbId)),
                        );
                        removeError(`pb-id-select-${id}`);
                    } else {
                        addError({
                            code: PB_ID_SUGGEST_ERROR,
                            message: VALIDATION_ERRORS.PB_ID_SUGGEST_ERROR,
                            controlId: `pb-id-select-${id}`,
                        });
                    }
                    setBusy(false);
                },
            );
        } else {
            setSuggestedPBIds(null);
        }
    }, [query, id, pbIds, addError, removeError, apiDispatch, entityType]);


    const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
    }, []);

    const clearSuggest = useCallback(() => {
        setQuery('');
        debouncedOnSelect('', id);
        removeError(`pb-id-select-${id}`);
        setShown(false);
        setBusy(false);
        cancellableRef.current.cancel();
    }, [id, removeError, debouncedOnSelect]);


    const onItemSelect = useCallback((entity: ReferenceEntity) => {
        debouncedOnSelect(entity.pbId, id);
        setQuery(entity.pbId);
        setSuggestedPBIds(null);
    }, [id, debouncedOnSelect]);

    return (
        <ValidatedControl name={`pb-id-select-${id}`}>
            {invalid => (
                <ErrorBoundary>
                    <Group gap="xl" alignItems="start">
                        <WidthItem width={width}>
                            <Autosuggest
                                disabled={disabled}
                                value={query}
                                shown={shown}
                                setShown={setShown}
                                color={invalid ? 'ERROR' : undefined}
                                placeholder={placeholder}
                                onChange={onChange}
                                onClear={clearSuggest}
                                onItemSelect={onItemSelect}
                                onClick={() => setShown(true)}
                                endAdornment={busy ? <Loading type={LoadingTypes.ICON}/> : undefined}>
                                {Array.isArray(suggestedPBIds) && suggestedPBIds.length > 0
                                    ? suggestedPBIds.map(entity => (
                                            <AutosuggestItem
                                                key={entity.pbId}
                                                value={entity}
                                                caption={entity.entityName}
                                                meta={entity.pbId}
                                            />
                                        ))
                                    : <AutosuggestNoResults/>}
                            </Autosuggest>
                        </WidthItem>
                        {pbIds.length > 1 && !disabled && (
                            <Item className="group-item pdy-xs">
                                <ErrorFlatButton
                                    icon={Icons.TIMES_CIRCLE}
                                    label="Remove"
                                    onClick={() => onRemove(id)}
                                />
                            </Item>
                        )}
                    </Group>
                </ErrorBoundary>
            )}
        </ValidatedControl>
    );
};
